import {
  ServerMessageSchema,
  WS_PING,
  WS_PING_INTERVAL_MS,
  reconnectDelayMs,
  type ServerMessage,
} from '@jetopolis/shared';

export type ConnectionState = 'connecting' | 'online' | 'offline';

interface Handlers {
  onMessage: (msg: ServerMessage) => void;
  onState: (state: ConnectionState) => void;
}

/**
 * Один сокет на мир.
 *
 * Переподключение с экспоненциальной задержкой и джиттером: без джиттера все
 * клиенты мира вернутся одновременно и добьют объект, который только что упал.
 *
 * Heartbeat отправляет строку ping, на которую объект отвечает автоответом, не
 * просыпаясь: так соединение остаётся живым, а мир продолжает спать.
 */
export class WorldSocket {
  private socket: WebSocket | null = null;
  private attempt = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closedByUs = false;
  private seq = 0;

  private readonly worldId: string;
  private readonly locale: string;
  private readonly handlers: Handlers;

  // Поля объявлены явно: erasableSyntaxOnly запрещает свойства из параметров
  // конструктора, потому что такой синтаксис не стирается, а компилируется.
  constructor(worldId: string, locale: string, handlers: Handlers) {
    this.worldId = worldId;
    this.locale = locale;
    this.handlers = handlers;
  }

  connect(): void {
    this.closedByUs = false;
    this.handlers.onState('connecting');
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}/ws/${this.worldId}?lng=${encodeURIComponent(this.locale)}`;
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempt = 0;
      this.handlers.onState('online');
      this.send('world.join', {});
      this.startPing();
    });

    socket.addEventListener('message', (e: MessageEvent<string>) => {
      // Автоответ pong приходит обычной строкой и схеме не подчиняется.
      if (e.data === 'pong') return;
      let raw: unknown;
      try {
        raw = JSON.parse(e.data);
      } catch {
        return;
      }
      const parsed = ServerMessageSchema.safeParse(raw);
      if (!parsed.success) {
        // Сообщение не из известного контракта: молчать нельзя, но и падать
        // из-за него тоже - возможно, сервер новее клиента.
        console.warn('[ws] неизвестное сообщение', raw);
        return;
      }
      this.handlers.onMessage(parsed.data);
    });

    socket.addEventListener('close', () => {
      this.stopPing();
      if (this.closedByUs) return;
      this.handlers.onState('offline');
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      // close придёт следом и запустит переподключение.
    });
  }

  private scheduleReconnect(): void {
    this.attempt += 1;
    this.reconnectTimer = setTimeout(
      () => {
        this.connect();
      },
      reconnectDelayMs(this.attempt, Math.random()),
    );
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(WS_PING);
    }, WS_PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  /** id команды строится клиентом и делает её идемпотентной (инвариант 8). */
  send(name: string, args: Record<string, unknown>): string {
    this.seq += 1;
    const id = `${Date.now().toString(36)}-${this.seq.toString(36)}`.padEnd(8, '0');
    this.socket?.send(JSON.stringify({ id, t: 'cmd', name, args }));
    return id;
  }

  close(): void {
    this.closedByUs = true;
    this.stopPing();
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = null;
  }
}
